import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Table } from '@opendate-app/setlist';
import SalesCard from './SalesCard';
import TeamPagination from '../../TeamManager/components/TeamPagination';
import TableSettings from '../../shared/components/TableSettings';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import { buildSalesFilterGroups, withRoomFilterGroup } from '../constants/salesFiltersConfig';
import TrendBadge from './TrendBadge';
import '../styles/reportingStyles.css';
import { KpiCardSkeleton, SalesCardSkeleton, BarChartSkeleton } from './Skeletons';
import ReportingPageLayout from './ReportingPageLayout';
import useReportingPage from '../hooks/useReportingPage';
import { currencyFormatter, compactCurrency, formatDate, getSalesChartGranularity } from '../utils/reportingUtils';

const axios = require('axios').default;
const qs = require('qs');

const groupedFieldsFrom = (columns = []) => columns.map((col, i) => ({
  id: col.key,
  label: col.label,
  defaultVisible: col.defaultVisible !== false,
  alwaysVisible: i === 0,
}));

const buildSalesApiParams = (activeFilters, dateMode, dateRange, granularityOverride) => {
  const params = {};

  Object.entries(activeFilters).forEach(([key, value]) => {
    if (key === 'venue_ownership_id' && Array.isArray(value) && value.length > 0) {
      params.venue_ownership_id = value;
    }
    if (key === 'calendar_event_id' && Array.isArray(value) && value.length > 0) {
      params.calendar_event_id = value;
    }
    if (key === 'tag_ids' && Array.isArray(value) && value.length > 0) {
      params.tag_ids = value;
    }
    if (key === 'room_ids' && Array.isArray(value) && value.length > 0) {
      params.room_ids = value;
    }
    if (key === 'payment_type' && Array.isArray(value) && value.length > 0) {
      params.payment_type = value;
    }
    if (key === 'sources' && Array.isArray(value) && value.length > 0) {
      params.sources = value;
    }
    if (key === 'team_membership_id' && Array.isArray(value) && value.length > 0) {
      params.team_membership_id = value;
    }
  });

  if (dateRange && dateRange.start) params.start_date = dateRange.start;
  if (dateRange && dateRange.end) params.end_date = dateRange.end;

  if (dateMode === 'event_date') {
    params.date_mode = 'event_date';
  }

  params.granularity = granularityOverride || getSalesChartGranularity(dateRange);

  return params;
};

function SalesDetailPage({ team, csrfToken, venueOwnerships, eventOptions, tagOptions, roomOptions, sellerOptions, config, detailParams }) {
  const {
    isMobile, sidebarOpen, setSidebarOpen,
    dateRange, setDateRange,
    filterSidebarOpen, setFilterSidebarOpen,
    activeFilters, comparison,
    handleFilterChange, handleRemoveFilter, handleClearAllFilters,
  } = useReportingPage(config.defaultPreset, { urlFilterIds: config.urlFilterIds });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [priorData, setPriorData] = useState(null);
  const [dateMode, setDateMode] = useState(
    (config.dateModeOptions && config.dateModeOptions.length > 0) ? config.dateModeOptions[0].key : 'order_date'
  );

  const [detailRows, setDetailRows] = useState([]);
  const [detailLoading, setDetailLoading] = useState(true);
  const [detailError, setDetailError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(config.defaultPerPage || 20);
  const [totalEntries, setTotalEntries] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [groupBy, setGroupBy] = useState(config.groupByDefault);
  const [groupByPopoverOpen, setGroupByPopoverOpen] = useState(false);
  const [eventRows, setEventRows] = useState([]);
  const [sortRules, setSortRules] = useState(config.defaultSortRules || [{ field: 'order_date', direction: 'desc' }]);
  const [sortPopoverOpen, setSortPopoverOpen] = useState(false);
  const [tableSettingsOpen, setTableSettingsOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(config.getDefaultVisibleColumns());
  const [hiddenColumns, setHiddenColumns] = useState(config.getDefaultHiddenColumns());

  const groupedFields = useMemo(() => groupedFieldsFrom(config.eventGroupedColumns), [config.eventGroupedColumns]);
  const [groupedVisibleColumns, setGroupedVisibleColumns] = useState(() => groupedFields.filter(f => f.defaultVisible));
  const [groupedHiddenColumns, setGroupedHiddenColumns] = useState(() => groupedFields.filter(f => !f.defaultVisible));
  const groupedColumnsStorageKey = (teamId) => `${config.localStorageKey(teamId)}_grouped`;

  const allColumns = config.columnsForGroupBy ? config.columnsForGroupBy(groupBy) : config.columns;
  const sortTypes = config.sortTypes;
  const isGroupedMode = groupBy === 'event' || groupBy === 'venue';

  const detailReqSeq = useRef(0);
  const groupByColumnsRef = useRef(groupBy);
  const scrollableTable = config.scrollableTable;
  const hasPageSize = config.pageSizeOptions && config.pageSizeOptions.length > 0;
  const nowrapCellStyle = scrollableTable ? { whiteSpace: 'nowrap' } : {};
  const stickyColumns = (scrollableTable && !isMobile)
    ? (config.stickyColumns || (config.firstColumnId ? [{ id: config.firstColumnId }] : []))
    : [];
  const stickyStyleFor = (colId, isHeader) => {
    const idx = stickyColumns.findIndex((s) => s.id === colId);
    if (idx === -1) return {};
    const left = stickyColumns.slice(0, idx).reduce((sum, s) => sum + (s.width || 0), 0);
    const self = stickyColumns[idx];
    return {
      position: 'sticky',
      left,
      zIndex: isHeader ? 4 : 3,
      background: 'var(--sl-bg-primary, #fff)',
      ...(self.width ? { minWidth: self.width, maxWidth: self.width, width: self.width } : {}),
    };
  };

  const salesFilterGroups = useMemo(
    () => withRoomFilterGroup(
      config.buildFilterGroups
        ? config.buildFilterGroups(venueOwnerships, eventOptions, tagOptions, sellerOptions)
        : buildSalesFilterGroups(venueOwnerships, eventOptions, tagOptions),
      roomOptions
    ),
    [venueOwnerships, eventOptions, tagOptions, roomOptions, sellerOptions, config.buildFilterGroups]
  );

  useEffect(() => {
    if (!team || !team.id) return;
    const saved = localStorage.getItem(config.localStorageKey(team.id));
    if (saved) {
      try {
        const { visible, hidden } = JSON.parse(saved);
        const knownIds = new Set(allColumns.map(c => c.id));
        const validVisible = visible.filter(c => knownIds.has(c.id));
        const validHidden = hidden.filter(c => knownIds.has(c.id));
        if (validVisible.length === 0) {
          setVisibleColumns(config.getDefaultVisibleColumns());
          setHiddenColumns(config.getDefaultHiddenColumns());
        } else {
          const savedIds = new Set([...validVisible.map(c => c.id), ...validHidden.map(c => c.id)]);
          const newColumns = allColumns.filter(c => !savedIds.has(c.id));
          setVisibleColumns(validVisible);
          setHiddenColumns([...validHidden, ...newColumns]);
        }
      } catch (e) {
      }
    }
  }, [team && team.id]);

  useEffect(() => {
    if (!team || !team.id || groupedFields.length === 0) return;
    try {
      const saved = localStorage.getItem(groupedColumnsStorageKey(team.id));
      if (!saved) return;
      const { visible, hidden } = JSON.parse(saved);
      const byId = new Map(groupedFields.map(f => [f.id, f]));
      const validVisible = visible.map(f => byId.get(f.id)).filter(Boolean);
      if (validVisible.length === 0) return;
      const validHidden = hidden.map(f => byId.get(f.id)).filter(Boolean);
      const savedIds = new Set([...validVisible, ...validHidden].map(f => f.id));
      setGroupedVisibleColumns(validVisible);
      setGroupedHiddenColumns([...validHidden, ...groupedFields.filter(f => !savedIds.has(f.id))]);
    } catch (e) {
    }
  }, [team && team.id]);

  useEffect(() => {
    if (!config.columnsForGroupBy) return;
    if (groupByColumnsRef.current === groupBy) return;
    groupByColumnsRef.current = groupBy;

    const nextColumns = config.columnsForGroupBy(groupBy);
    const nextIds = new Set(nextColumns.map(c => c.id));
    const keptVisible = visibleColumns.filter(c => nextIds.has(c.id));
    const keptHidden = hiddenColumns.filter(c => nextIds.has(c.id));
    const knownIds = new Set([...keptVisible, ...keptHidden].map(c => c.id));
    const added = nextColumns.filter(c => !knownIds.has(c.id));

    setVisibleColumns([...keptVisible, ...added.filter(c => c.defaultVisible)]);
    setHiddenColumns([...keptHidden, ...added.filter(c => !c.defaultVisible)]);

    const sortableIds = new Set(nextColumns.filter(c => c.sortable).map(c => c.id));
    if (sortRules.some(rule => !sortableIds.has(rule.field))) {
      setSortRules(config.defaultSortRules || []);
    }
  }, [groupBy]);

  useEffect(() => {
    if (!team || !team.id || config.kpiFromDetailResponse) return;
    if (config.detailOnly) { setLoading(false); return; }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        if (csrfToken) {
          axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
        }

        const params = buildSalesApiParams(activeFilters, dateMode, dateRange);

        const [currentRes, priorRes] = await Promise.all([
          axios.get(`/teams/${team.id}/reporting/dashboard/sales/data.json`, { params: { ...params, sections: ['top_events', 'venue_series'] } }),
          comparison.start ? axios.get(`/teams/${team.id}/reporting/dashboard/sales/data.json`, {
            params: { ...buildSalesApiParams(activeFilters, dateMode, { start: comparison.start, end: comparison.end }, getSalesChartGranularity(dateRange)), sections: 'metrics' },
          }) : Promise.resolve(null),
        ]);

        setData(currentRes.data);
        setPriorData(priorRes ? priorRes.data : null);
      } catch (e) {
        setError('Failed to load sales data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [team && team.id, csrfToken, activeFilters, dateMode, dateRange]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortRules, groupBy, activeFilters, dateRange, dateMode, detailParams]);

  const buildDetailApiParams = useCallback(() => {
    const params = {
      page: currentPage,
      per_page: perPage,
      sort_by: sortRules.map(r => r.field),
      sort_direction: sortRules.map(r => r.direction),
    };

    if (searchTerm) params.search = searchTerm;

    if (config.buildDetailApiFilters) {
      Object.assign(params, config.buildDetailApiFilters(activeFilters));
    } else {
      if (activeFilters.venue_ownership_id && activeFilters.venue_ownership_id.length > 0) {
        params.venue_ownership_id = activeFilters.venue_ownership_id;
      }
      if (activeFilters.calendar_event_id && activeFilters.calendar_event_id.length > 0) {
        params.calendar_event_id = activeFilters.calendar_event_id;
      }
      if (activeFilters.tag_ids && activeFilters.tag_ids.length > 0) {
        params.tag_ids = activeFilters.tag_ids;
      }
      if (activeFilters.room_ids && activeFilters.room_ids.length > 0) {
        params.room_ids = activeFilters.room_ids;
      }
      if (activeFilters.sources && activeFilters.sources.length > 0) {
        params.sources = activeFilters.sources;
      }
      if (activeFilters.team_membership_id && activeFilters.team_membership_id.length > 0) {
        params.team_membership_id = activeFilters.team_membership_id;
      }
    }

    if (activeFilters.payment_type && activeFilters.payment_type.length > 0) {
      params.payment_type = activeFilters.payment_type;
    }

    if (dateRange && dateRange.start) params.start_date = dateRange.start;
    if (dateRange && dateRange.end) params.end_date = dateRange.end;

    if (dateMode) {
      params.date_mode = dateMode;
    }

    const individualKey = config.groupByOptions && config.groupByOptions.find(o => o.key !== 'event' && o.key !== 'venue')?.key;
    if (groupBy && groupBy !== individualKey) {
      params.group_by = groupBy;
    }

    if (config.kpiFromDetailResponse && comparison.start && comparison.end) {
      params.prior_start_date = comparison.start;
      params.prior_end_date = comparison.end;
    }

    return { ...params, ...detailParams };
  }, [currentPage, perPage, sortRules, searchTerm, activeFilters, dateRange, dateMode, groupBy, comparison, detailParams]);

  useEffect(() => {
    if (!team || !team.id) return;

    const fetchDetail = async () => {
      const seq = ++detailReqSeq.current;
      try {
        setDetailLoading(true);
        setDetailError(null);
        if (csrfToken) {
          axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
        }

        const params = buildDetailApiParams();
        const response = await axios.get(config.detailEndpoint(team.id), {
          params,
          paramsSerializer: (p) => qs.stringify(p, { arrayFormat: 'brackets' }),
        });

        if (seq !== detailReqSeq.current) return;
        const responseData = response.data || {};
        if (config.kpiFromDetailResponse && responseData.kpis) {
          setData(responseData.kpis);
          if (responseData.prior_kpis) {
            setPriorData(responseData.prior_kpis);
          }
          setLoading(false);
        }
        if (groupBy === 'event' || groupBy === 'venue') {
          setEventRows(responseData.events || responseData.venues || []);
          setDetailRows([]);
        } else {
          setDetailRows(responseData[config.detailResponseKey] || []);
          setEventRows([]);
        }
        setTotalEntries(responseData.pagination?.total_entries || 0);
        setTotalPages(responseData.pagination?.total_pages || 1);
      } catch (e) {
        if (seq !== detailReqSeq.current) return;
        setDetailError(config.detailErrorMessage);
        if (config.kpiFromDetailResponse) {
          setError(config.detailErrorMessage);
        }
      } finally {
        if (seq === detailReqSeq.current) {
          setDetailLoading(false);
          if (config.kpiFromDetailResponse) {
            setLoading(false);
          }
        }
      }
    };

    fetchDetail();
  }, [team && team.id, csrfToken, currentPage, perPage, sortRules, searchTerm, activeFilters, dateRange, dateMode, groupBy, detailParams]);

  const buildSalesExportUrl = () => {
    const urlParams = new URLSearchParams({ section: config.salesExportSection });
    const apiParams = buildSalesApiParams(activeFilters, dateMode, dateRange);
    if (apiParams.start_date) urlParams.set('start_date', apiParams.start_date);
    if (apiParams.end_date) urlParams.set('end_date', apiParams.end_date);
    if (apiParams.venue_ownership_id) {
      const ids = Array.isArray(apiParams.venue_ownership_id) ? apiParams.venue_ownership_id : [apiParams.venue_ownership_id];
      ids.forEach(id => urlParams.append('venue_ownership_id[]', id));
    }
    if (apiParams.tag_ids) {
      const ids = Array.isArray(apiParams.tag_ids) ? apiParams.tag_ids : [apiParams.tag_ids];
      ids.forEach(id => urlParams.append('tag_ids[]', id));
    }
    if (apiParams.room_ids) {
      apiParams.room_ids.forEach(id => urlParams.append('room_ids[]', id));
    }
    if (apiParams.payment_type) {
      const pts = Array.isArray(apiParams.payment_type) ? apiParams.payment_type : [apiParams.payment_type];
      pts.forEach(pt => urlParams.append('payment_type[]', pt));
    }
    if (apiParams.date_mode) urlParams.set('date_mode', apiParams.date_mode);
    return `/teams/${team.id}/reporting/dashboard/sales/export?${urlParams.toString()}`;
  };

  const buildDetailExportUrl = () => {
    const urlParams = new URLSearchParams();
    const apiParams = buildDetailApiParams();
    const skipKeys = new Set(['page', 'per_page', 'prior_start_date', 'prior_end_date']);
    Object.entries(apiParams).forEach(([key, value]) => {
      if (skipKeys.has(key) || value == null) return;
      if (Array.isArray(value)) {
        value.forEach(v => urlParams.append(`${key}[]`, v));
      } else {
        urlParams.set(key, value);
      }
    });
    return `${config.exportBasePath(team.id)}?${urlParams.toString()}`;
  };

  const handleColumnSort = (colId) => {
    const col = allColumns.find(c => c.id === colId);
    if (!col || !col.sortable) return;

    setSortRules(prev => {
      const existing = prev.length === 1 && prev[0].field === colId;
      if (existing) {
        return [{ field: colId, direction: prev[0].direction === 'asc' ? 'desc' : 'asc' }];
      }
      return [{ field: colId, direction: 'asc' }];
    });
  };

  const sortableColumns = allColumns.filter(c => c.sortable);

  const handleSortRuleChange = (index, key, value) => {
    setSortRules(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [key]: value };
      return updated;
    });
  };

  const handleAddSortRule = () => {
    const usedFields = new Set(sortRules.map(r => r.field));
    const nextCol = sortableColumns.find(c => !usedFields.has(c.id));
    if (nextCol) {
      setSortRules(prev => [...prev, { field: nextCol.id, direction: 'asc' }]);
    }
  };

  const handleRemoveSortRule = (index) => {
    setSortRules(prev => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleGroupedColumnsChange = (newVisible, newHidden) => {
    setGroupedVisibleColumns(newVisible);
    setGroupedHiddenColumns(newHidden);
    localStorage.setItem(groupedColumnsStorageKey(team.id), JSON.stringify({
      visible: newVisible,
      hidden: newHidden,
    }));
  };

  const handleColumnsChange = (newVisible, newHidden) => {
    setVisibleColumns(newVisible);
    setHiddenColumns(newHidden);
    localStorage.setItem(config.localStorageKey(team.id), JSON.stringify({
      visible: newVisible,
      hidden: newHidden,
    }));
  };

  useEffect(() => {
    if (!sortPopoverOpen) return;
    const handleClickOutside = (e) => {
      if (sortPopoverOpen && !e.target.closest('.reporting-sort-popover')) {
        setSortPopoverOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [sortPopoverOpen]);

  useEffect(() => {
    if (!groupByPopoverOpen) return;
    const handleClickOutside = (e) => {
      if (!e.target.closest('.reporting-groupby-popover')) {
        setGroupByPopoverOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [groupByPopoverOpen]);

  const sortArrow = (colId) => {
    const rule = sortRules.find(r => r.field === colId);
    if (!rule) return null;
    const index = sortRules.indexOf(rule);
    const arrow = rule.direction === 'asc' ? '\u2191' : '\u2193';
    return sortRules.length > 1 ? ` ${index + 1}${arrow}` : ` ${arrow}`;
  };

  const formatEventDate = (dateStr) => {
    if (!dateStr) return '\u2014';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      ...(team.time_zone ? { timeZone: team.time_zone } : {}),
    });
  };

  const formatEventDateTime = (dateStr) => {
    if (!dateStr) return '\u2014';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      ...(team.time_zone ? { timeZone: team.time_zone } : {}),
    });
  };

  const formatters = { formatEventDate, formatEventDateTime, currencyFormatter, team, dateRange, activeFilters };
  const activeHiddenColumns = isGroupedMode ? groupedHiddenColumns : hiddenColumns;
  const groupedTableColumns = groupedVisibleColumns.map((field, i) => ({
    key: field.id,
    header: field.label,
    primary: i === 0,
    render: (row) => config.renderEventCell(field.id, row, formatters),
  }));

  const safeData = data || {};
  const salesData = config.kpiFromDetailResponse ? safeData : (safeData[config.salesDataKey] || {});
  const venueSeries = config.kpiFromDetailResponse ? null : (safeData.venue_series || null);
  const venueNames = venueSeries ? venueSeries.names : [];

  const safePrior = priorData || {};
  const priorSalesData = config.kpiFromDetailResponse ? safePrior : (safePrior[config.salesDataKey] || {});
  const cmpLabel = config.comparisonLabel || comparison.label;

  const kpiCards = config.buildKpiCards(salesData, priorSalesData);

  const dateModeOptions = config.dateModeOptions || [
    { key: 'order_date', label: 'Order Date' },
    { key: 'event_date', label: 'Event Date' },
  ];

  const viewByToggle = dateModeOptions.length < 2 ? null : (
    <>
      <div style={{ width: 1, height: 20, background: 'var(--sl-border-primary, #d0d5dd)', flexShrink: 0 }} />
      <span style={{ fontSize: 13, color: 'var(--sl-text-tertiary, #667085)', fontWeight: 500, whiteSpace: 'nowrap' }}>View by:</span>
      <div style={{
        display: 'inline-flex',
        background: 'var(--sl-bg-tertiary, #f2f4f7)',
        borderRadius: 8,
        padding: 2,
        position: 'relative',
        height: 32,
      }}>
        {dateModeOptions.map(({ key, label }) => {
          const active = dateMode === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setDateMode(key)}
              style={{
                fontSize: 13,
                fontWeight: active ? 600 : 400,
                padding: '0 12px',
                borderRadius: 6,
                border: 'none',
                background: active ? 'var(--sl-bg-primary, #ffffff)' : 'transparent',
                color: active ? 'var(--sl-text-secondary, #344054)' : 'var(--sl-text-tertiary, #667085)',
                cursor: 'pointer',
                position: 'relative',
                zIndex: 1,
                boxShadow: active ? '0 1px 2px rgba(16, 24, 40, 0.06), 0 1px 3px rgba(16, 24, 40, 0.1)' : 'none',
                transition: 'all 0.15s ease',
                lineHeight: '28px',
              }}
            >
              {label}
            </button>
          );
        })}
      </div>
    </>
  );

  return (
    <ReportingPageLayout
      team={team}
      activeKey={config.sidebarKey}
      title={config.pageTitle}
      subtitle={config.pageSubtitle}
      dateRange={dateRange}
      setDateRange={setDateRange}
      isMobile={isMobile}
      sidebarOpen={sidebarOpen}
      setSidebarOpen={setSidebarOpen}
      filterSidebarOpen={filterSidebarOpen}
      setFilterSidebarOpen={setFilterSidebarOpen}
      activeFilters={activeFilters}
      filterGroups={salesFilterGroups}
      handleFilterChange={handleFilterChange}
      handleRemoveFilter={handleRemoveFilter}
      handleClearAllFilters={handleClearAllFilters}
      filterTitle={config.filterTitle}
      filterSubtitle={config.filterSubtitle}
      toolbarExtras={(
        <>
          {viewByToggle}
          {config.renderToolbarExtras ? config.renderToolbarExtras({ team, csrfToken, dateRange, activeFilters }) : null}
        </>
      )}
      showDatePicker={config.showDatePicker !== false}
      showFilters={config.showFilters !== false}
    >
          {error && (
            <div className="alert alert-danger">{error}</div>
          )}

          {config.renderNotice && config.renderNotice(activeFilters, { team, csrfToken, dateRange })}

          {loading ? (
            <>
              {config.kpiGridColumns > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : `repeat(${config.kpiGridColumns}, 1fr)`, gap: 12, marginBottom: 24 }}>
                  {Array.from({ length: config.kpiGridColumns }, (_, i) => <KpiCardSkeleton key={i} showBottom={false} />)}
                </div>
              )}
              {config.overTimeKey && <SalesCardSkeleton />}
              {(config.topCharts || []).length > 0 && (
                <div className="d-flex mb-4" style={{ gap: 16, flexDirection: isMobile ? 'column' : 'row' }}>
                  <BarChartSkeleton />
                  <BarChartSkeleton />
                </div>
              )}
            </>
          ) : !error && (
            <>
              {kpiCards.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : `repeat(${config.kpiGridColumns}, 1fr)`, gap: 12, marginBottom: 24 }}>
                {kpiCards.map((card) => (
                  <div key={card.label} style={{ background: 'var(--sl-bg-primary, #fff)', border: '1px solid var(--sl-border-secondary, #e4e7ec)', borderRadius: 8, padding: '16px 16px 14px' }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--sl-text-tertiary, #667085)', marginBottom: 8 }}>
                      {card.label}
                    </div>
                    <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--sl-text-primary, #101828)', lineHeight: 1.2, marginBottom: 6 }}>
                      {card.value}
                    </div>
                    <TrendBadge current={card.current} prior={card.prior} label={cmpLabel} />
                  </div>
                ))}
              </div>
              )}

              {config.overTimeKey && (
              <SalesCard
                title={config.salesCardTitle}
                metricValue={config.buildMetricValue(salesData)}
                metricLabel={config.buildMetricLabel(salesData)}
                chartData={salesData[config.overTimeKey] || []}
                chartColor={config.chartColor}
                isCurrency={config.salesCardIsCurrency !== false}
                countLabel={config.countLabel}
                seriesData={venueSeries ? venueSeries[config.venueSeriesKey] : null}
                seriesNames={venueNames}
                priorChartData={(safePrior.granularity === safeData.granularity && priorSalesData[config.overTimeKey]) || []}
                dateRange={dateRange}
              />
              )}

              {Array.from({ length: Math.ceil((config.topCharts || []).length / 2) }, (_, rowIdx) => (
              <div key={rowIdx} className="d-flex mb-4" style={{ gap: 16, flexDirection: isMobile ? 'column' : 'row' }}>
                {config.topCharts.slice(rowIdx * 2, rowIdx * 2 + 2).map((chart) => {
                  const chartData = safeData[chart.dataKey] || [];
                  return (
                    <div key={chart.dataKey} className="card" style={{ flex: 1, minWidth: 0 }}>
                      <div className="card-body">
                        <h6 className="card-title mb-3" style={{ fontWeight: 600 }}>{chart.title}</h6>
                        {chartData.length === 0 ? (
                          <div className="text-center py-4 text-muted">No data available.</div>
                        ) : (
                          <ResponsiveContainer width="100%" height={Math.max(250, chartData.length * 36)}>
                            <BarChart
                              data={chartData}
                              layout="vertical"
                              margin={{ top: 0, right: 20, left: 10, bottom: 0 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                              <XAxis
                                type="number"
                                tick={{ fontSize: 12 }}
                                allowDecimals={false}
                                {...(chart.isCurrency ? { tickFormatter: (v) => currencyFormatter.format(v) } : {})}
                              />
                              <YAxis
                                type="category"
                                dataKey={chart.nameKey || "event_name"}
                                width={140}
                                tick={{ fontSize: 12 }}
                                tickFormatter={(v) => v && v.length > 20 ? v.slice(0, 20) + '...' : (v || '')}
                              />
                              <Tooltip
                                cursor={false}
                                content={({ active, payload }) => {
                                  if (!active || !payload || !payload.length) return null;
                                  const eventName = payload[0].payload[chart.nameKey || 'event_name'];
                                  return (
                                    <div style={{ background: '#000', borderRadius: 8, padding: 12, minWidth: 140 }}>
                                      <p style={{ color: 'var(--sl-text-white, #fff)', fontSize: 12, margin: '0 0 8px 0' }}>{eventName}</p>
                                      <div className="d-flex align-items-center" style={{ gap: 6 }}>
                                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: chart.barColor, flexShrink: 0 }} />
                                        <span style={{ color: '#a1a1aa', fontSize: 12 }}>{chart.tooltipLabel}:</span>
                                        <span style={{ color: 'var(--sl-text-white, #fff)', fontSize: 12, fontWeight: 600 }}>
                                          {chart.isCurrency ? currencyFormatter.format(payload[0].value) : payload[0].value.toLocaleString()}
                                        </span>
                                      </div>
                                    </div>
                                  );
                                }}
                              />
                              <Bar dataKey={chart.barDataKey} fill={chart.barColor} radius={[0, 4, 4, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              ))}
            </>
          )}

          <div style={{ background: 'var(--sl-bg-primary, #fff)', border: '1px solid var(--sl-border-secondary, #e4e7ec)', borderRadius: 8, marginTop: 16 }}>
            <div className="d-flex align-items-center justify-content-between" style={{ padding: '16px 24px', borderBottom: '1px solid var(--sl-bg-tertiary, #f2f4f7)' }}>
              <h6 style={{ fontWeight: 700, fontSize: 16, margin: 0, color: 'var(--sl-text-primary, #101828)' }}>{isGroupedMode ? (groupBy === 'event' ? 'Events' : 'Venues') : config.detailTableTitle}</h6>
              <div className="d-flex align-items-center" style={{ gap: 8 }}>
                <a
                  href={buildDetailExportUrl()}
                  className="btn btn-sm d-flex align-items-center"
                  style={{ gap: 6, fontSize: 13, fontWeight: 500, padding: '4px 12px', borderRadius: 6, border: '1px solid var(--sl-border-primary, #d0d5dd)', background: 'var(--sl-bg-primary, #fff)', color: 'var(--sl-text-secondary, #344054)', height: 32, textDecoration: 'none' }}
                >
                  Export CSV <i className="fas fa-download" style={{ fontSize: 12 }} />
                </a>
                <div style={{ position: 'relative' }}>
                  <button
                    type="button"
                    className="btn btn-sm d-flex align-items-center"
                    onClick={() => setTableSettingsOpen(!tableSettingsOpen)}
                    style={{ fontSize: 13, fontWeight: 500, padding: '4px 12px', borderRadius: 6, border: '1px solid var(--sl-border-primary, #d0d5dd)', background: 'var(--sl-bg-primary, #fff)', color: 'var(--sl-text-secondary, #344054)', height: 32 }}
                  >
                    <i className="fas fa-columns mr-2" style={{ fontSize: 12 }} />
                    Table Settings
                    {activeHiddenColumns.length > 0 && (
                      <span className="ml-1" style={{ background: 'var(--sl-fg-brand, #1982c4)', color: 'var(--sl-text-white, #fff)', borderRadius: 10, padding: '0 6px', fontSize: 11, fontWeight: 600, lineHeight: '18px' }}>
                        {activeHiddenColumns.length}
                      </span>
                    )}
                  </button>
                  <TableSettings
                    isOpen={tableSettingsOpen}
                    onClose={() => setTableSettingsOpen(false)}
                    visibleFields={isGroupedMode ? groupedVisibleColumns : visibleColumns}
                    hiddenFields={activeHiddenColumns}
                    onFieldsChange={isGroupedMode ? handleGroupedColumnsChange : handleColumnsChange}
                  />
                </div>
                {config.groupByOptions && config.groupByOptions.length > 0 && (
                <div className="reporting-groupby-popover" style={{ position: 'relative' }}>
                  <button
                    type="button"
                    className="btn btn-sm d-flex align-items-center"
                    onClick={() => setGroupByPopoverOpen(!groupByPopoverOpen)}
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      padding: '4px 12px',
                      borderRadius: 6,
                      border: isGroupedMode ? '1px solid var(--sl-fg-brand, #1982c4)' : '1px solid var(--sl-border-primary, #d0d5dd)',
                      background: isGroupedMode ? 'var(--sl-bg-brand-primary, #f6fafe)' : 'var(--sl-bg-primary, #fff)',
                      color: isGroupedMode ? 'var(--sl-fg-brand, #1982c4)' : 'var(--sl-text-secondary, #344054)',
                      height: 32,
                    }}
                  >
                    <i className="fas fa-layer-group mr-2" style={{ fontSize: 12 }} />
                    Group by
                  </button>
                  {groupByPopoverOpen && (
                    <div
                      className="card shadow-sm"
                      style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        left: 'auto',
                        zIndex: 1050,
                        minWidth: 180,
                        marginTop: 4,
                      }}
                    >
                      <div className="card-body p-2">
                        {config.groupByOptions.map(({ key, label }) => (
                          <button
                            key={key}
                            type="button"
                            className="btn btn-sm btn-block text-left d-flex align-items-center justify-content-between"
                            onClick={() => { setGroupBy(key); setGroupByPopoverOpen(false); setCurrentPage(1); }}
                            style={{
                              fontSize: 13,
                              fontWeight: groupBy === key ? 600 : 400,
                              padding: '6px 12px',
                              borderRadius: 6,
                              border: 'none',
                              background: groupBy === key ? 'var(--sl-bg-brand-primary, #f6fafe)' : 'transparent',
                              color: groupBy === key ? 'var(--sl-fg-brand, #1982c4)' : 'var(--sl-text-secondary, #344054)',
                            }}
                          >
                            {label}
                            {groupBy === key && <i className="fas fa-check" style={{ fontSize: 12, color: 'var(--sl-fg-brand, #1982c4)' }} />}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                )}
                <div className="reporting-sort-popover" style={{ position: 'relative' }}>
                  <button
                    type="button"
                    className="btn btn-sm d-flex align-items-center"
                    onClick={() => setSortPopoverOpen(!sortPopoverOpen)}
                    style={{ fontSize: 13, fontWeight: 500, padding: '4px 12px', borderRadius: 6, border: '1px solid var(--sl-border-primary, #d0d5dd)', background: 'var(--sl-bg-primary, #fff)', color: 'var(--sl-text-secondary, #344054)', height: 32 }}
                  >
                    <i className="fas fa-sort mr-2" style={{ fontSize: 12 }} />
                    Sort{sortRules.length > 1 ? ` (${sortRules.length})` : ''}
                  </button>
                  {sortPopoverOpen && (
                    <div
                      className="card shadow-sm"
                      style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        left: 'auto',
                        zIndex: 1050,
                        minWidth: isMobile ? 280 : 420,
                        marginTop: 4,
                      }}
                    >
                      <div className="card-body p-3">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <span className="font-weight-bold">Sort by</span>
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 text-muted"
                            onClick={() => setSortPopoverOpen(false)}
                          >
                            <i className="fal fa-times" />
                          </button>
                        </div>
                        {sortRules.map((rule, index) => {
                          const col = allColumns.find(c => c.id === rule.field);
                          const sortType = col?.sortType || 'text';
                          const dirLabels = sortTypes[sortType] || sortTypes.text;
                          return (
                            <div key={index} className="d-flex align-items-center gap-2 mb-2">
                              <select
                                className="reporting-select"
                                style={{ flex: '1 1 0' }}
                                value={rule.field}
                                onChange={(e) => handleSortRuleChange(index, 'field', e.target.value)}
                              >
                                {sortableColumns.map(c => (
                                  <option
                                    key={c.id}
                                    value={c.id}
                                    disabled={sortRules.some((r, i) => i !== index && r.field === c.id)}
                                  >
                                    {c.label}
                                  </option>
                                ))}
                              </select>
                              <select
                                className="reporting-select"
                                style={{ flex: '1 1 0' }}
                                value={rule.direction}
                                onChange={(e) => handleSortRuleChange(index, 'direction', e.target.value)}
                              >
                                <option value="asc">{dirLabels.asc}</option>
                                <option value="desc">{dirLabels.desc}</option>
                              </select>
                              <button
                                type="button"
                                className="btn btn-link btn-sm p-0 text-muted"
                                onClick={() => handleRemoveSortRule(index)}
                                disabled={sortRules.length <= 1}
                                style={{ opacity: sortRules.length <= 1 ? 0.3 : 1 }}
                              >
                                <i className="fal fa-times" />
                              </button>
                            </div>
                          );
                        })}
                        {sortRules.length < sortableColumns.length && (
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 text-primary"
                            onClick={handleAddSortRule}
                          >
                            <i className="fal fa-plus mr-1" />
                            Add another sort
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div style={{ padding: '12px 24px', borderBottom: '1px solid var(--sl-bg-tertiary, #f2f4f7)' }}>
              <div className="d-flex align-items-center justify-content-between">
                <div style={{ position: 'relative', maxWidth: 400, flex: 1 }}>
                  <i className="fas fa-search" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--sl-text-disabled, #98a2b3)', fontSize: 14 }} />
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder={isGroupedMode ? (groupBy === 'event' ? 'Search events...' : 'Search venues...') : config.searchPlaceholder}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ paddingLeft: 36, border: '1px solid var(--sl-border-secondary, #e4e7ec)', borderRadius: 8, height: 40 }}
                  />
                </div>
                <span style={{ fontSize: 13, color: 'var(--sl-text-tertiary, #667085)', fontWeight: 500 }}>
                  {totalEntries.toLocaleString()} {isGroupedMode ? (groupBy === 'event' ? `event${totalEntries !== 1 ? 's' : ''}` : `venue${totalEntries !== 1 ? 's' : ''}`) : config.itemLabel(totalEntries)}
                </span>
              </div>
            </div>

            {isGroupedMode ? (
              <div className="reporting-nowrap-table">
                <Table
                  columns={groupedTableColumns}
                  data={eventRows}
                  getRowKey={(row) => row.event_id || row.venue_ownership_id || row.venue_id || row.event_name || row.venue_name}
                  loading={detailLoading}
                  error={detailError || undefined}
                  empty="No data found."
                  stickyFirstColumn={!isMobile}
                />
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead>
                    <tr>
                      {visibleColumns.map((col) => {
                        const colConfig = allColumns.find(c => c.id === col.id);
                        const isSortable = colConfig && colConfig.sortable;
                        return (
                          <th
                            key={col.id}
                            style={{
                              fontWeight: 500,
                              fontSize: 12,
                              color: 'var(--sl-text-tertiary, #667085)',
                              textTransform: 'uppercase',
                              ...nowrapCellStyle,
                              ...(col.id === config.firstColumnId ? { paddingLeft: 24 } : {}),
                              ...stickyStyleFor(col.id, true),
                              ...(isSortable ? { cursor: 'pointer', userSelect: 'none' } : {}),
                            }}
                            onClick={isSortable ? () => handleColumnSort(col.id) : undefined}
                            title={colConfig && colConfig.headerTooltip ? colConfig.headerTooltip : undefined}
                          >
                            {col.label}
                            {colConfig && colConfig.headerTooltip && (
                              <i className="fal fa-info-circle ml-1" style={{ fontSize: 11, color: 'var(--sl-text-disabled, #98a2b3)' }} />
                            )}
                            {sortArrow(col.id)}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {detailLoading ? (
                      <tr><td colSpan={visibleColumns.length} className="text-center py-4 text-muted">Loading...</td></tr>
                    ) : detailError ? (
                      <tr><td colSpan={visibleColumns.length} className="text-center py-4 text-muted">{detailError}</td></tr>
                    ) : detailRows.length === 0 ? (
                      <tr><td colSpan={visibleColumns.length} className="text-center py-4 text-muted">{config.emptyMessage}</td></tr>
                    ) : detailRows.map((row) => (
                      <tr key={row.id}>
                        {visibleColumns.map(col => (
                          <td key={col.id} style={{ fontSize: 14, color: 'var(--sl-text-secondary, #344054)', ...nowrapCellStyle, ...(col.id === config.firstColumnId ? { paddingLeft: 24, fontWeight: 600, color: 'var(--sl-text-primary, #101828)' } : {}), ...stickyStyleFor(col.id, false) }}>
                            {config.renderCellValue(col, row, formatters)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {(totalPages > 1 || hasPageSize) && (
              <div className={hasPageSize ? 'd-flex align-items-center justify-content-between' : undefined} style={{ padding: '12px 24px', borderTop: '1px solid var(--sl-bg-tertiary, #f2f4f7)' }}>
                {hasPageSize && (
                  <div className="d-flex align-items-center" style={{ gap: 8 }}>
                    <span style={{ fontSize: 13, color: 'var(--sl-text-tertiary, #667085)' }}>Rows per page</span>
                    <select
                      value={perPage}
                      onChange={(e) => { setPerPage(Number(e.target.value)); setCurrentPage(1); }}
                      className="reporting-select"
                      style={{ width: 'auto' }}
                    >
                      {config.pageSizeOptions.map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                )}
                {totalPages > 1 && (
                  <TeamPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={totalEntries}
                    pageSize={perPage}
                    itemLabel={config.paginationLabel}
                    onPageChange={(page) => {
                      if (!detailLoading && page >= 1 && page <= totalPages) {
                        setCurrentPage(page);
                      }
                    }}
                  />
                )}
              </div>
            )}
          </div>

    </ReportingPageLayout>
  );
}

export default SalesDetailPage;

